<!-- ImportarBg.vue
     Montado en menuForm.vue (siempre en el DOM, nunca se destruye).
     El diálogo y el upload viven aquí, independientes del árbol de carpetas.
     El estado viene 100% del singleton useImportarMinimizado.
-->
<template>
  <v-dialog v-model="dialogVisible" max-width="700px" persistent>
    <v-card>

      <!-- ── HEADER estilo Windows (─  ×) ── -->
      <v-card-title
        class="text-h6 text-white d-flex align-center py-2 px-4"
        style="background-color: #004d82; gap: 4px;"
      >
        <v-icon class="mr-2" size="20">mdi-file-import</v-icon>
        Importar
        <span v-if="nombreCarpeta" class="ml-1" style="font-size:13px;opacity:.75;">
          — {{ nombreCarpeta }}
        </span>
        <v-spacer></v-spacer>

        <!-- Minimizar -->
        <v-btn
          icon size="small" variant="text"
          title="Minimizar (seguir en segundo plano)"
          @click="minimizar"
        >
          <v-icon color="white" size="18">mdi-minus</v-icon>
        </v-btn>

        <!-- Cerrar -->
        <v-btn
          icon size="small" variant="text"
          title="Cerrar"
          @click="cerrar"
        >
          <v-icon color="white" size="18">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- ── BODY ── -->
      <v-card-text class="pt-6">

        <!-- Plantilla -->
        <v-select
          v-model="plantillaSeleccionada"
          :items="plantillas"
          item-title="plantilla"
          item-value="codigo"
          label="Plantilla"
          variant="outlined"
          density="comfortable"
          class="mb-4"
          :disabled="plantillaBloqueada || subiendo"
          :readonly="plantillaBloqueada"
        ></v-select>

        <!-- Volumen -->
        <v-select
          v-model="volumenSeleccionado"
          :items="volumenes"
          item-title="pathTexto"
          item-value="volId"
          label="Volumen"
          variant="outlined"
          density="comfortable"
          return-object
          class="mb-4"
          :disabled="subiendo"
        ></v-select>

        <!-- Selector de archivos -->
        <v-file-input
          v-model="archivosSeleccionados"
          label="Arrastre archivos aquí"
          multiple
          variant="outlined"
          prepend-icon="mdi-paperclip"
          show-size
          class="mb-4"
          :disabled="subiendo"
          @update:model-value="actualizarTablaArchivos"
        >
          <template v-slot:selection="{ fileNames }">
            <div style="max-height:100px;overflow-y:auto;display:flex;flex-wrap:wrap;gap:4px;padding:2px 0;width:100%">
              <v-chip
                v-for="fn in fileNames" :key="fn"
                size="small" label color="primary" class="me-2"
              >{{ fn }}</v-chip>
            </div>
          </template>
        </v-file-input>

        <!-- Tabla de archivos -->
        <v-data-table
          :headers="headersArchivos"
          :items="archivosTabla"
          density="compact"
          :items-per-page="5"
          class="elevation-1 mb-4"
          height="200px"
          no-data-text="Sin archivos seleccionados"
        >
          <template v-slot:[`item.size`]="{ item }">
            {{ formatFileSize(item.size) }}
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <v-chip :color="getStatusColor(item.status)" size="small" label>
              {{ item.status }}
            </v-chip>
          </template>
        </v-data-table>

        <!-- Barra de progreso -->
        <div v-if="subiendo || completado" class="mb-3">
          <div class="d-flex justify-space-between mb-1">
            <span class="text-caption text-grey-darken-1">{{ progressSize }}</span>
            <span class="text-caption font-weight-bold">{{ progreso }}%</span>
          </div>
          <v-progress-linear
            :model-value="progreso"
            :color="completado ? 'success' : '#004d82'"
            height="10"
            rounded
          ></v-progress-linear>
          <p v-if="completado" class="text-caption text-success mt-1 d-flex align-center ga-1">
            <v-icon size="14" color="success">mdi-check-circle</v-icon>
            Subida completada — puedes cerrar este diálogo
          </p>
          <p v-else class="text-caption text-grey mt-1 d-flex align-center ga-1">
            <v-icon size="14" color="primary">mdi-information-outline</v-icon>
            Puedes minimizar y seguir usando el sistema
          </p>
        </div>

        <!-- Error -->
        <v-alert
          v-if="errorSubida"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
        >{{ errorSubida }}</v-alert>

        <!-- OCR / AV -->
        <div v-if="tieneArchivoAV" class="mt-2">
          <v-divider class="mb-3"></v-divider>
          <template v-if="configOCRActiva">
            <p class="text-body-2 text-grey-darken-2 mb-2 d-flex align-center ga-1">
              <v-icon size="16" color="#004d82">mdi-microphone</v-icon>
              Se detectó audio/video. Selecciona el idioma para la transcripción:
            </p>
            <v-select
              v-model="idiomaSeleccionado"
              :items="idiomasOCR"
              item-title="title"
              item-value="value"
              label="Idioma del audio"
              prepend-icon="mdi-translate"
              variant="outlined"
              density="comfortable"
              :disabled="subiendo"
            ></v-select>
          </template>
          <template v-else>
            <v-alert type="warning" variant="tonal" density="compact" icon="mdi-microphone-off">
              No tienes API para OCR de audio y video
            </v-alert>
          </template>
        </div>

      </v-card-text>

      <!-- ── FOOTER ── -->
      <v-card-actions class="px-6 pb-4">
        <v-spacer></v-spacer>
        <v-btn
          color="#ce501b"
          variant="elevated"
          :loading="subiendo"
          :disabled="!puedeSubir || completado"
          @click="iniciarSubida"
        >
          Comenzar Subida
        </v-btn>
        <v-btn
          color="#9B9B9B"
          variant="elevated"
          @click="cerrar"
        >
          {{ subiendo ? 'Cancelar subida' : 'Cancelar' }}
        </v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script setup>
import { useImportarMinimizado } from '@/components/experimental/useImportarMinimizado'

const {
  dialogVisible,
  nombreCarpeta,
  plantillas,
  volumenes,
  plantillaSeleccionada,
  volumenSeleccionado,
  plantillaBloqueada,
  archivosSeleccionados,
  archivosTabla,
  subiendo,
  progreso,
  progressSize,
  completado,
  errorSubida,
  tieneArchivoAV,
  configOCRActiva,
  idiomaSeleccionado,
  puedeSubir,
  formatFileSize,
  getStatusColor,
  minimizar,
  cerrar,
  actualizarTablaArchivos,
  iniciarSubida,
} = useImportarMinimizado()

const headersArchivos = [
  { title: 'Nombre de archivo', key: 'name',   sortable: false },
  { title: 'Tamaño',            key: 'size',   sortable: false },
  { title: 'Estado',            key: 'status', sortable: false },
]

const idiomasOCR = [
  { title: 'Español',    value: 'es' },
  { title: 'Inglés',     value: 'en' },
  { title: 'Portugués',  value: 'pt' },
  { title: 'Francés',    value: 'fr' },
  { title: 'Alemán',     value: 'de' },
  { title: 'Italiano',   value: 'it' },
  { title: 'Chino',      value: 'zh' },
  { title: 'Japonés',    value: 'ja' },
  { title: 'Coreano',    value: 'ko' },
  { title: 'Árabe',      value: 'ar' },
]
</script>
